import type { PlayerInput, PlayerSide } from './core';
import {
  WAVEBEAN_SOCKET_PATH,
  normalizeRoomCode,
  parseServerMessage,
  type ClientMessage,
  type ServerMessage,
} from './protocol';

export type WavebeanConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface WavebeanNetworkHandlers {
  onConnection(state: WavebeanConnectionState): void;
  onMessage(message: ServerMessage): void;
}

type InitialCommand = Extract<ClientMessage, { type: 'create' | 'join' }>;

const emptyInput = (): PlayerInput => ({ left: false, right: false, jump: false, attack: false });

function sameInput(left: PlayerInput, right: PlayerInput): boolean {
  return left.left === right.left
    && left.right === right.right
    && left.jump === right.jump
    && left.attack === right.attack;
}

export function wavebeanPublicOrigin(): string {
  if (window.location.hostname.endsWith('.chatgpt.site')) return 'https://oneultool.com';
  return window.location.origin;
}

function socketUrl(): string {
  const serviceOrigin = wavebeanPublicOrigin();
  const url = new URL(WAVEBEAN_SOCKET_PATH, serviceOrigin);
  url.protocol = serviceOrigin.startsWith('https:') ? 'wss:' : 'ws:';
  return url.toString();
}

function tokenKey(roomCode: string): string {
  return `wavebean-room-${roomCode}`;
}

function storedToken(roomCode: string): string | undefined {
  try {
    return window.sessionStorage.getItem(tokenKey(roomCode)) ?? undefined;
  } catch {
    return undefined;
  }
}

function saveToken(roomCode: string, token: string): void {
  try {
    window.sessionStorage.setItem(tokenKey(roomCode), token);
  } catch {
    // Private browsing policies can disable storage; the current socket still works.
  }
}

function forgetToken(roomCode: string): void {
  try {
    window.sessionStorage.removeItem(tokenKey(roomCode));
  } catch {
    // Storage is optional.
  }
}

export class WavebeanNetwork {
  readonly #handlers: WavebeanNetworkHandlers;
  #socket: WebSocket | null = null;
  #generation = 0;
  #reconnectTimer: number | null = null;
  #reconnectStartedAt = 0;
  #reconnectAttempt = 0;
  #intentionalClose = false;
  #allowReconnect = false;
  #roomCode: string | null = null;
  #token: string | null = null;
  #side: PlayerSide | null = null;
  #sequence = 0;
  #lastInput = emptyInput();
  #lastInputSentAt = 0;

  constructor(handlers: WavebeanNetworkHandlers) {
    this.#handlers = handlers;
  }

  get roomCode(): string | null {
    return this.#roomCode;
  }

  get side(): PlayerSide | null {
    return this.#side;
  }

  createRoom(): void {
    this.#resetConnection(false);
    this.#connect({ type: 'create' }, false);
  }

  joinRoom(value: string): void {
    const roomCode = normalizeRoomCode(value);
    this.#resetConnection(false);
    this.#roomCode = roomCode;
    const token = storedToken(roomCode);
    this.#token = token ?? null;
    this.#connect({ type: 'join', roomCode, ...(token ? { token } : {}) }, false);
  }

  sendInput(input: Readonly<PlayerInput>): void {
    if (this.#side === null || this.#socket?.readyState !== WebSocket.OPEN) return;
    const now = performance.now();
    const next = { ...input };
    if (sameInput(next, this.#lastInput) && now - this.#lastInputSentAt < 150) return;
    this.#lastInput = next;
    this.#lastInputSentAt = now;
    this.#send({ type: 'input', seq: this.#sequence, input: next });
    this.#sequence += 1;
  }

  requestRematch(): void {
    this.#send({ type: 'rematch' });
  }

  leave(): void {
    const roomCode = this.#roomCode;
    if (this.#socket?.readyState === WebSocket.OPEN) this.#send({ type: 'leave' });
    this.#resetConnection(true);
    if (roomCode) forgetToken(roomCode);
  }

  dispose(): void {
    this.#resetConnection(false);
  }

  #connect(command: InitialCommand, reconnecting: boolean): void {
    const generation = ++this.#generation;
    this.#intentionalClose = false;
    this.#handlers.onConnection(reconnecting ? 'reconnecting' : 'connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(socketUrl());
    } catch {
      this.#handlers.onConnection('offline');
      return;
    }
    this.#socket = socket;

    socket.addEventListener('open', () => {
      if (generation !== this.#generation) return;
      socket.send(JSON.stringify(command));
    });
    socket.addEventListener('message', (event) => {
      if (generation !== this.#generation || typeof event.data !== 'string') return;
      const message = parseServerMessage(event.data);
      if (!message) return;
      if (message.type === 'room') {
        this.#roomCode = message.roomCode;
        this.#token = message.token;
        this.#side = message.side;
        this.#sequence = 0;
        this.#allowReconnect = true;
        this.#reconnectAttempt = 0;
        this.#reconnectStartedAt = 0;
        saveToken(message.roomCode, message.token);
        this.#handlers.onConnection('connected');
      } else if (message.type === 'closed') {
        this.#allowReconnect = false;
      } else if (message.type === 'error' && !message.retryable) {
        this.#allowReconnect = false;
      }
      this.#handlers.onMessage(message);
    });
    socket.addEventListener('close', () => {
      if (generation !== this.#generation) return;
      this.#socket = null;
      if (this.#intentionalClose) return;
      if (this.#allowReconnect && this.#roomCode && this.#token) {
        this.#scheduleReconnect();
      } else {
        this.#handlers.onConnection('offline');
      }
    });
    socket.addEventListener('error', () => {
      // The close event owns retry and user-facing state transitions.
    });
  }

  #scheduleReconnect(): void {
    if (!this.#roomCode || !this.#token) return;
    if (this.#reconnectStartedAt === 0) this.#reconnectStartedAt = performance.now();
    if (performance.now() - this.#reconnectStartedAt > 19_000) {
      this.#allowReconnect = false;
      this.#handlers.onConnection('offline');
      return;
    }
    this.#handlers.onConnection('reconnecting');
    const delay = Math.min(3_000, 400 * 2 ** this.#reconnectAttempt);
    this.#reconnectAttempt += 1;
    this.#reconnectTimer = window.setTimeout(() => {
      this.#reconnectTimer = null;
      if (!this.#roomCode || !this.#token) return;
      this.#connect({ type: 'join', roomCode: this.#roomCode, token: this.#token }, true);
    }, delay);
  }

  #send(message: ClientMessage): void {
    if (this.#socket?.readyState !== WebSocket.OPEN) return;
    this.#socket.send(JSON.stringify(message));
  }

  #resetConnection(sendClosedState: boolean): void {
    this.#generation += 1;
    this.#intentionalClose = true;
    this.#allowReconnect = false;
    if (this.#reconnectTimer !== null) window.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#reconnectStartedAt = 0;
    this.#reconnectAttempt = 0;
    this.#socket?.close();
    this.#socket = null;
    this.#roomCode = null;
    this.#token = null;
    this.#side = null;
    this.#sequence = 0;
    this.#lastInput = emptyInput();
    this.#lastInputSentAt = 0;
    if (sendClosedState) this.#handlers.onConnection('offline');
  }
}
